import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import * as StepsActions from './steps.actions';
import * as StepsSelectors from './steps.selectors';
import { Observable } from 'rxjs';
import { GroupedSessions, Session, SessionTask } from '@tekman/onmat/models';

@Injectable()
export class StepsFacade {
  updateStep$: Observable<Session[]> = this._store.pipe(select(StepsSelectors.getUpdateSessions));
  progress$ = this._store.pipe(select(StepsSelectors.getStudentExercisesProgress));

  constructor(private readonly _store: Store) {}

  sessions$ = (stepId: string) =>
    this._store.pipe(
      select(StepsSelectors.getSessionsLoaded(stepId)),
      filter(steps => !!steps)
    );

  sessionsLoaded$ = () => this._store.pipe(select(StepsSelectors.isSessionsLoaded));

  evaluationInstruments$ = (stepId: string) =>
    this._store.pipe(
      select(StepsSelectors.getEvaluationInstrumentsLoaded(stepId)),
      filter(steps => !!steps)
    );

  otherActivities$ = (stepId: string) =>
    this._store.pipe(
      select(StepsSelectors.getOtherActivitiesLoaded(stepId)),
      filter(steps => !!steps)
    );

  initSessions(itineraryId: string, stepId: string, groupId: string): void {
    this._store.dispatch(StepsActions.loadSessions({ itineraryId, stepId, groupId }));
    this._store.dispatch(StepsActions.loadEvaluationInstruments({ stepId, groupId }));
    this._store.dispatch(StepsActions.loadOtherActivities({ itineraryId, stepId, groupId }));
  }

  orderedSessionsSuccess(): void {
    this._store.dispatch(StepsActions.orderedSessionsSuccess());
  }

  resetSessionsLoaded(): void {
    this._store.dispatch(StepsActions.resetSessionsLoaded());
  }

  updateSteps(itineraryId: string, stepId: string, groupedSessions: GroupedSessions[], groupId: string) {
    const sessions = this.mapGroupedSessionToSession(groupedSessions);
    this._store.dispatch(StepsActions.updateSteps({ itineraryId, stepId, sessions, groupId }));
  }

  private mapGroupedSessionToSession(groupedSessions: GroupedSessions[]): Session[] {
    return groupedSessions.map(session => {
      return {
        items: [
          ...this.getItems(session.consultation ?? []),
          ...this.getItems(session.experimentation ?? []),
          ...this.getItems(session.evaluation ?? []),
          ...this.getItems(session.preparation ?? []),
          ...this.getItems(session.practice ?? []),
        ],
        order: session.order,
      } as Session;
    });
  }

  private getItems(sessionTask: SessionTask[]) {
    return sessionTask.map(session => {
      return { id: session.activity?.id, order: session.order ?? 0, type: session.type ?? 1 };
    });
  }
}
